/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Link, Typography } from '@mui/material';
import React, { forwardRef, RefObject, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { createRef } from 'react';
import { i18n } from '../../i18n/i18n';
import { obj } from '../../models/common-types';
import FilterChip from './components/FilterChip/FilterChip';
import MultiSelectionFilter from './components/MultiSelectionFilter/MultiSelectionFilter';
import SingleSelectionFilter from './components/SingleSelectionFilter/SingleSelectionFilter';
import { Filter, FilterOption, FiltersHandlers, FiltersPanelHandlers, FiltersState } from './filter-types';

type FiltersRefsObject = obj<RefObject<FiltersHandlers>>;

interface FiltersPanelProps {
    filters: Filter[];
    onApplyFilters: (filtersToApply: FiltersState) => void;
    hideChips?: boolean;
}

const FiltersPanel = forwardRef<FiltersPanelHandlers, FiltersPanelProps>(
    ({ filters, onApplyFilters, hideChips }, forwardedRef) => {
        const [filtersRefs, setFiltersRefs] = useState<FiltersRefsObject>({});
        const [filtersState, setFiltersState] = useState<FiltersState>(null);
        const [calculateExpand, setCalculateExpand] = useState<boolean>(false);
        const [expandIndex, setExpandIndex] = useState<number>(0);
        const [expanded, setExpanded] = useState<boolean>(false);
        const [finalExpandIndex, setFinalExpandIndex] = useState<number>(0);
        const containerRef = useRef(null);

        useImperativeHandle(forwardedRef, () => ({
            updateFilters: (state: FiltersState) => setFiltersState(state),
            filtersState,
        }));

        useEffect(() => {
            if (filters.length) {
                const refsObject: FiltersRefsObject = {};
                filters.forEach((filter) => (refsObject[filter.originalName] = createRef<FiltersHandlers>()));
                setFiltersRefs(refsObject);
            }
        }, [filters]);

        useEffect(() => {
            filtersState && onApplyFilters(filtersState);
        }, [filtersState]);

        useEffect(() => {
            if (filters.length && filtersState) {
                setExpandIndex(0);
                setCalculateExpand(true);
            }
        }, [filters, filtersState]);

        useEffect(() => {
            if (calculateExpand && containerRef.current) {
                if (containerRef.current.offsetHeight > 50) {
                    setFinalExpandIndex(expandIndex);
                    setCalculateExpand(false);
                } else {
                    if (expandIndex + 1 > filterChips.length) {
                        setCalculateExpand(false);
                        setExpandIndex(filterChips.length);
                        setFinalExpandIndex(filterChips.length);
                    } else {
                        setExpandIndex(expandIndex + 1);
                    }
                }
            }
        }, [calculateExpand, expandIndex]);

        const filterChips = useMemo(() => {
            const result = [];
            filtersState &&
                Object.entries(filtersState)
                    .filter((filter) => filter[1])
                    .map(([filterName, options], index) => {
                        return Array.isArray(options)
                            ? options.map((option, innerIndex) =>
                                  result.push(
                                      <FilterChip
                                          key={`${index}_${innerIndex}`}
                                          filterOption={{
                                              filter: filters.find((filter) => filter.originalName === filterName),
                                              optionValue: option,
                                          }}
                                          onCloseChip={(name, optionValue) => {
                                              const filterdOptions = (filtersState[name!!] as FilterOption[]).filter(
                                                  (option: FilterOption) => option.value !== optionValue!!.value,
                                              );
                                              filtersRefs[filterName].current?.updateChosenSelection(filterdOptions);
                                              setFiltersState((prev) => ({
                                                  ...prev,
                                                  [filterName]: filterdOptions.length ? filterdOptions : null,
                                              }));
                                          }}
                                      />,
                                  ),
                              )
                            : result.push(
                                  <FilterChip
                                      key={index}
                                      filterOption={{
                                          filter: filters.find((filter) => filter.originalName === filterName),
                                          optionValue: options!!,
                                      }}
                                      onCloseChip={(filterName, value) => {
                                          filtersRefs[filterName!!].current?.updateChosenSelection(null);
                                          setFiltersState((prev) => ({
                                              ...prev,
                                              [filterName!!]: null,
                                          }));
                                      }}
                                  />,
                              );
                    });
            return result;
        }, [filters, filtersState, filtersRefs]);

        const expandChip = useMemo(() => {
            return (
                <Box
                    className="border-color-b background-color-blst background-color-hover color-b"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        border: '1px solid',
                        borderRadius: '40px',
                        px: 1,
                        mr: 1,
                        mb: 1,
                        justifyContent: 'space-between',
                    }}
                    onClick={() => {
                        setExpanded(!expanded);
                    }}
                >
                    <Typography sx={{ fontWeight: '500', textWrap: 'nowrap', cursor: 'pointer' }} className="color-b">
                        {expanded ? 'Show Less' : `+${filterChips.length - finalExpandIndex}`}
                    </Typography>
                </Box>
            );
        }, [filterChips, finalExpandIndex, expanded]);

        const expandChipDisplay = useMemo(() => {
            return filterChips.length - finalExpandIndex > 0;
        }, [calculateExpand, filterChips, finalExpandIndex, expandIndex]);
        const getFilter = (filter: Filter) => {
            if (filtersState?.[filter.originalName]) {
                filter.options.map((option) => {
                    // @ts-ignore
                    if (filtersState[filter.originalName].map((option) => option.label).includes(option.label)) {
                        option.initialChecked = true;
                    }
                    return option;
                });
            }
            return filter;
        };
        return (
            <Box sx={{ width: '100%' }}>
                <Box sx={{ display: 'flex', mb: 2, columnGap: 1 }}>
                    {filters
                        .filter((filter) => filter.options.length)
                        .map((filter, index) =>
                            filter.selectionType === 'multi' ? (
                                <MultiSelectionFilter
                                    key={index}
                                    ref={filtersRefs[filter.originalName]}
                                    filter={getFilter(filter)}
                                    onApply={(filterName: string, selectedValues: FilterOption[]) =>
                                        setFiltersState((prev) => ({
                                            ...prev,
                                            [filterName]: !selectedValues.length ? null : selectedValues,
                                        }))
                                    }
                                />
                            ) : (
                                <SingleSelectionFilter
                                    key={index}
                                    ref={filtersRefs[filter.originalName]}
                                    filter={filter}
                                    onApply={(filterName: string, selectedValue: FilterOption | null) => {
                                        setFiltersState((prev) => ({
                                            ...prev,
                                            [filterName]: selectedValue,
                                        }));
                                    }}
                                />
                            ),
                        )}
                </Box>

                <Box
                    sx={{ pl: 2, pb: 1, flexWrap: 'wrap', display: hideChips ? 'none' : 'flex' }}
                    className="background-color-l-md chips-container"
                >
                    {expanded ? filterChips : filterChips.slice(0, finalExpandIndex)}
                    {!!expandChipDisplay && expandChip}
                    {filtersState && Object.values(filtersState!!).find((value) => value) && (
                        <Link
                            sx={{ ml: 1 }}
                            onClick={() => {
                                filtersState &&
                                    Object.keys(filtersState as object).forEach((filterName) => {
                                        filtersRefs[filterName].current?.resetChosenSelection();
                                    });
                                setFiltersState({});
                                setExpanded(false);
                            }}
                        >
                            {i18n.translate('Clear all')}
                        </Link>
                    )}
                </Box>

                {calculateExpand && (
                    <Box
                        sx={{
                            pl: 2,
                            pb: 1,
                            display: 'flex',
                            flexWrap: 'wrap',
                            height: 0,
                            padding: 0,
                            visiblity: 'hidden',
                        }}
                        className="background-color-l-md chips-container"
                    >
                        <Box
                            ref={containerRef}
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                position: 'relative',
                                top: `${window.innerHeight}px`,
                            }}
                        >
                            {filterChips.slice(0, expandIndex + 1)}
                            {filterChips.length && expandChip}
                            {filtersState && Object.values(filtersState!!).find((value) => value) && (
                                <Link sx={{ ml: 1 }}>{i18n.translate('Clear all')}</Link>
                            )}
                        </Box>
                    </Box>
                )}
            </Box>
        );
    },
);

export default FiltersPanel;
